import type { AddressParts } from '../thaiAddress'
import type { MemberWelcome } from '../../member/memberJoin'
import type { MemberSurvey, SurveyAnswers, SurveyConfig } from '../../member/joinSurvey'
import { MEMBER_API_URL } from '../../config/env'
import { readMemberSession } from '../../member/memberSession'
import { resolveBotIdFromHref } from '../orderUtils'
import type { MemberOrdersResponse } from '../../member/myOrdersLogic'

async function postMemberApi<T>(body: Record<string, unknown>): Promise<{ status: number; data: T }> {
  const response = await fetch(MEMBER_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = (await response.json().catch(() => ({ ok: false }))) as T
  return { status: response.status, data }
}

export function fetchMyOrders(idToken: string, botId: string) {
  return postMemberApi<MemberOrdersResponse>({ action: 'listMyOrders', idToken, botId })
}

/** แอดมินดูมุมมองลูกค้า — CF ตรวจว่า LINE นี้อยู่ในรายชื่อแอดมินทุกครั้ง */
export function fetchCustomerOrders(idToken: string, orderId: string) {
  return postMemberApi<MemberOrdersResponse>({ action: 'listCustomerOrders', idToken, orderId })
}

export type MemberUiEvent =
  | { event: 'announcements_closed'; seen: Array<{ id: string; version: number }>; optOut: string[] }
  | { event: 'announcement_click'; id: string }
  | { event: 'activities_seen'; entries: Array<{ id: string; version: number }> }
  | { event: 'activity_click'; id: string }

/** ป้ายประกาศ/ถุงทอง — ไม่ block UI ถ้าส่งไม่สำเร็จ (keepalive ให้ส่งได้แม้กำลังเปิดลิงก์ออก) */
export async function sendMemberUiEvent(ev: MemberUiEvent): Promise<void> {
  try {
    const idToken = await currentMemberIdToken()
    if (!idToken) return
    await fetch(MEMBER_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'memberUiEvent', idToken, ...ev }),
      keepalive: true,
    })
  } catch {
    /* สถิติ/การจำการปิด พลาดได้ — ครั้งหน้าป้ายจะขึ้นอีกเท่านั้น */
  }
}

/**
 * ข้อมูลกรอกล่วงหน้าของเจ้าของออร์เดอร์ (คนหลัก · ที่อยู่หลัก · ผู้ร่วมพิธีที่เคยกรอก)
 * idToken = LINE ของเจ้าของ/LINE แอดมิน · ไม่มี LINE = ส่ง Firebase token staff ของออร์เดอร์นี้ (แอดมินหลังบ้าน)
 */
export async function fetchOrderPrefill(orderId: string, auth: { idToken?: string; staffToken?: string }) {
  const response = await fetch(MEMBER_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(auth.staffToken && !auth.idToken ? { Authorization: `Bearer ${auth.staffToken}` } : {}),
    },
    body: JSON.stringify({ action: 'orderPrefill', orderId, ...(auth.idToken ? { idToken: auth.idToken } : {}) }),
  })
  const data = (await response.json().catch(() => ({ ok: false }))) as {
    ok?: boolean
    prefill?: import('../orderPrefill').OrderPrefill | null
  }
  return response.ok && data.ok ? data.prefill || null : null
}

export function fetchViewerStatus(idToken: string) {
  return postMemberApi<{ ok: boolean; isAdminViewer?: boolean }>({ action: 'viewerStatus', idToken })
}

export interface SavedAddress {
  id: string
  label: string
  recipientName: string
  phone: string
  address: string
  /** ที่อยู่แยกช่อง — CF ส่งเฉพาะเมื่อตรงกับ address */
  addressParts?: AddressParts | null
  isDefault: boolean
}

/** ตัวเลือกในแผ่น "เลือกที่อยู่" — profile = ที่อยู่หลักสมาชิก · saved = สำรอง · history = ข้อมูลจากการบูชาครั้งล่าสุด */
export interface AddressChoice {
  id: string
  kind: 'profile' | 'saved' | 'history'
  label: string
  recipientName: string
  phone: string
  address: string
  addressParts?: AddressParts | null
  birthday?: string
}

export type AddressChoicesResponse = {
  ok: boolean
  choices?: AddressChoice[]
  /** LINE แอดมินเปิดออร์เดอร์ลูกค้า → ตัวเลือกเป็นของเจ้าของออร์เดอร์ */
  staff?: boolean
  notLinked?: boolean
  canAddSaved?: boolean
  message?: string
}

/** CF ตรวจสิทธิ์เอง — ส่ง orderId ได้เสมอ (ใช้เฉพาะเมื่อเป็น LINE แอดมิน) */
export function fetchAddressChoices(idToken: string, orderId: string) {
  return postMemberApi<AddressChoicesResponse>({ action: 'addressChoices', idToken, orderId })
}

export interface MemberProfile {
  recipientName: string
  phone: string
  address: string
  /** ที่อยู่แยกช่อง — CF ส่งเฉพาะเมื่อตรงกับ address · ส่งตอนบันทึกได้ (CF เก่าข้าม) */
  addressParts?: AddressParts | null
  birthday: string
  needsSetup: boolean
  source: string
  /** หญิง / ชาย / ไม่ระบุ · CF เก่าไม่ส่ง */
  gender?: string
  survey?: MemberSurvey | null
}

/** welcome = สมัครสมาชิกครั้งแรก (ยืนยันข้อมูลครบเองครั้งแรก) → พิธีต้อนรับ · server ส่งครั้งเดียวต่อคน */
type MemberProfileResponse = { ok: boolean; profile?: MemberProfile; message?: string; welcome?: MemberWelcome; surveyConfig?: SurveyConfig; surveyBonus?: number }

type AddressResponse = { ok: boolean; addresses?: SavedAddress[]; message?: string }

export function fetchSavedAddresses(idToken: string) {
  return postMemberApi<AddressResponse>({ action: 'listAddresses', idToken })
}

export function saveSavedAddress(idToken: string, address: Omit<SavedAddress, 'isDefault'> | Omit<SavedAddress, 'id' | 'isDefault'>) {
  // CF จำกัดเพิ่มใหม่ 2 ชุด (address_limit) · แก้ชุดเดิมส่ง id
  return postMemberApi<AddressResponse>({ action: 'saveAddress', idToken, address })
}

export function deleteSavedAddress(idToken: string, addressId: string) {
  return postMemberApi<AddressResponse>({ action: 'deleteAddress', idToken, addressId })
}

export function setDefaultSavedAddress(idToken: string, addressId: string) {
  return postMemberApi<AddressResponse>({ action: 'setDefaultAddress', idToken, addressId })
}

export function fetchMemberProfile(idToken: string) {
  return postMemberApi<MemberProfileResponse>({ action: 'getMemberProfile', idToken })
}

export function claimBirthdayGift(idToken: string) {
  return postMemberApi<{ ok: boolean; status?: 'pending' | 'fulfilled'; message?: string }>({ action: 'claimBirthdayGift', idToken })
}

export function saveMemberProfile(
  idToken: string,
  profile: Omit<MemberProfile, 'needsSetup' | 'source' | 'survey'>,
  survey?: { answers: SurveyAnswers; beliefConsent: boolean } | null,
) {
  return postMemberApi<MemberProfileResponse>({ action: 'saveMemberProfile', idToken, profile, ...(survey ? { survey } : {}) })
}

export async function currentMemberIdToken(): Promise<string> {
  if (window.__OC_MEMBER_HARNESS__) return window.__OC_MEMBER_HARNESS__.idToken
  const session = readMemberSession(resolveBotIdFromHref() || '1', window.location.search)
  if (session) return session.idToken
  const liff = (await import('@line/liff')).default
  return liff.getIDToken() || ''
}
